import { FC } from 'react'

import {
  Avatar,
  Box,
  Button,
  EnhancedDimension,
  SearchField,
  Text,
} from '@factorialco/deprecated-design-system'
import { PeopleChartTeamSelectorFragment } from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

type Props = {
  onChange: (teamId: string) => void
  onAddTeamClicked: () => void
  teams: PeopleChartTeamSelectorFragment[]
  width?: EnhancedDimension | undefined
}

const getFilteredTeams = (search: string, teams: PeopleChartTeamSelectorFragment[]) => {
  return teams.filter((team) => {
    const teamName = team.name.toLowerCase() || ''
    const filterByName = teamName.includes(search.toLowerCase())
    return filterByName
  })
}

const TeamSelector: FC<Props> = ({ onChange, teams, width, onAddTeamClicked }) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const handleClick = (teamId: string) => {
    onChange(String(teamId))
  }

  const filteredTeams = useUnsafeMemo(
    () => getFilteredTeams(debouncedSearch, teams),
    [debouncedSearch, teams]
  )

  const hasScroll = filteredTeams.length > 4

  return (
    <Box
      borderRadius={{
        all: 'abs016',
      }}
      background="white"
      paddingTop="s16"
      paddingBottom={hasScroll ? null : 's16'}
      paddingX="s16"
      gap="s8"
      width={width}
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
    >
      <Box justifyContent="spaceBetween" flexDirection="row" alignItems="center">
        <Text.Header3>{i18n.t('people_chart.group_selector.select_team')}</Text.Header3>
        <Button iconName={'add'} onClick={onAddTeamClicked}>
          {i18n.t('people_chart.team_selector.add_team')}
        </Button>
      </Box>
      <SearchField
        onSearch={onSearch}
        placeholder={i18n.t('people_chart.group_selector.search_teams')}
        search={search}
        loading={search !== debouncedSearch}
      />

      <Box overflowY="auto" height="s300">
        {filteredTeams.length === 0 && (
          <Box alignItems="center" paddingX="s12" paddingY="s16">
            <Text.Normal>{i18n.t('people_chart.team_selector.no_teams')}</Text.Normal>
          </Box>
        )}

        {filteredTeams?.map((team) => (
          <Box
            onClick={() => handleClick(team.id)}
            key={team.id}
            flexDirection="row"
            gap="s12"
            paddingX="s16"
            paddingY="s12"
            cursor="pointer"
            alignItems="center"
          >
            <Box>
              <Avatar size="s32" label={team.name} url={buildUrl(team.avatar)} />
            </Box>
            <Box>
              <Text.Normal weight="bold">{team.name}</Text.Normal>
              <Text.Normal>
                {i18n.t('people_chart.group_selector.team_employees', {
                  memberships: String(team.members.totalCount || 0),
                })}
              </Text.Normal>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default TeamSelector
